import { Component, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Auth } from '@aws-amplify/auth';
import { AuthService } from '../auth/auth.service';
import { CommonUtil } from '../util/common-util';
import { ToastUtil } from '../util/toast-util';
import { Constant } from '../constant';
import { UnionConsultationListItem } from '../model/union-consultation';

@Component({
  selector: 'app-union-consultation',
  templateUrl: './union-consultation.component.html',
  styleUrl: './union-consultation.component.css',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatProgressSpinnerModule,
  ]
})
export class UnionConsultationComponent implements OnInit {
  public readonly Constant = Constant;
  public showSpinner = false;
  public isCreating = false;

  public items: UnionConsultationListItem[] = [];
  private page = 0;
  private hasMore = true;
  private isLoading = false;

  constructor(
    private title: Title,
    private router: Router,
    private commonUtil: CommonUtil,
    private toastUtil: ToastUtil,
    private auth: AuthService,
  ) {
    this.title.setTitle(Constant.pageTitleUnionConsultation + Constant.pageTitleCommon);
  }

  @HostListener('window:focus')
  onFocus(): void {
    this.commonUtil.checkRefreshToken(this.auth);
  }

  @HostListener('window:scroll')
  onScroll(): void {
    const scrollBottom = document.documentElement.scrollHeight - window.innerHeight - window.scrollY;
    if (scrollBottom < 300 && !this.isLoading && this.hasMore) {
      this.loadItems();
    }
  }

  ngOnInit(): void {
    window.scroll(0, 0);
    this.loadItems();
  }

  onCreate(): void {
    if (!this.commonUtil.checkOnline()) return;
    this.router.navigate(['/union-consultation', 'new']);
  }

  onItemClick(item: UnionConsultationListItem): void {
    this.router.navigate(['/union-consultation', item.id]);
  }

  getTruncatedSummary(summary: string): string {
    if (!summary) return '';
    const flat = summary.replace(/\n/g, ' ');
    if (flat.length <= Constant.ucSummaryTruncateLen) return flat;
    return flat.substring(0, Constant.ucSummaryTruncateLen) + '...';
  }

  // 相談日は初回と最終を表示、3つ以上は間を省略
  getDisplayConsultDates(dates: string[]): string {
    if (!dates || dates.length === 0) return '';
    if (dates.length === 1) return this.formatDate(dates[0]);
    const first = this.formatDate(dates[0]);
    const last = this.formatDate(dates[dates.length - 1]);
    if (dates.length === 2) return `${first}\n${last}`;
    return `${first}\n…\n${last}`;
  }

  private formatDate(date: string): string {
    return date ? date.replace(/-/g, '/') : '';
  }

  private loadItems(): void {
    if (this.isLoading || !this.hasMore) return;
    if (!this.commonUtil.checkOnline()) {
      return;
    }

    this.isLoading = true;
    if (this.page === 0) this.showSpinner = true;

    Auth.currentSession().then(session => {
      const options = this.auth.createApiHeader(session);
      const path = `${Constant.apiPathUnionConsultation}?page=${this.page}&size=${Constant.ucPageSize}`;
      this.commonUtil.apiGet(path, options)
        .then(res => {
          const data: UnionConsultationListItem[] = res.data ?? [];
          this.items = [...this.items, ...data];
          this.hasMore = data.length === Constant.ucPageSize;
          this.page++;
          this.showSpinner = false;
          this.isLoading = false;
        })
        .catch(err => {
          this.commonUtil.debug().log(err);
          if (err.message === Constant.msgLoginFailed) {
            this.commonUtil.showReloadDialog(this.auth);
          } else {
            this.showSpinner = false;
            this.isLoading = false;
            this.toastUtil.showErrorToast('', Constant.msgNetworkError, Constant.toastShowMiliSec);
          }
        });
    });
  }
}
